import * as React from "react";
import { Book, Download, Eye, X } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useGetAllResourcesQuery } from "../store/resourcesApi";

const AssetsSection = () => {
  const { data, isLoading, isError } = useGetAllResourcesQuery();
  const [selectedResource, setSelectedResource] = React.useState(null);

  React.useEffect(() => {
    if (selectedResource) {
      const element = document.getElementById("Resource");
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  });

  // Handle loading and error states
  if (isLoading)
    return <div className="p-8 text-center">Loading resources...</div>;
  if (isError)
    return (
      <div className="p-8 text-center text-red-500">
        Error loading resources
      </div>
    );

  const allResources = data?.data || [];

  const viewResource = (id) => {
    const resource = allResources.find((resource) => resource._id === id);
    setSelectedResource(resource);
  };

  const closeDetails = () => {
    setSelectedResource(null);
  };

  return (
    <div className="p-4 sm:p-8 bg-gradient-to-br from-background to-green-200 dark:bg-background dark:to-black min-h-screen">
      <div className="max-w-4xl mx-auto">
        <h2 className="text-3xl font-bold mb-2 text-green-800 dark:text-primary">
          Resource Center
        </h2>
        <p className="mb-8 text-green-600">
          Download and view resources for your journey
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10 ">
          {allResources.map((asset) => (
            <Card
              key={asset._id}
              className="group cursor-pointer relative overflow-hidden hover:shadow-2xl transition-all duration-500 ease-out border-green-100 backdrop-blur-sm"
            >
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-green-400 to-emerald-500 transform origin-left transition-transform duration-500 scale-x-0 group-hover:scale-x-100" />

              <div className="p-6">
                <div className="flex flex-col items-center text-center space-y-4">
                  <div className="p-4 rounded-full bg-green-100 text-green-600 transform transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3">
                    <Book size={24} />
                  </div>

                  <div className="space-y-2">
                    <h3 className="text-xl font-semibold text-green-800 dark:text-primary">
                      {asset.title}
                    </h3>
                    <Badge
                      variant="secondary"
                      className="bg-green-100 text-green-700 hover:bg-green-200"
                    >
                      {asset.name}
                    </Badge>
                    <p className="text-green-600 text-sm">
                      {asset.description}
                    </p>
                  </div>

                  <button
                    onClick={() => viewResource(asset._id)}
                    className="inline-flex items-center px-6 py-2 rounded-full bg-green-600 text-white hover:bg-green-700 transition-all duration-300 transform hover:-translate-y-1 hover:shadow-lg"
                  >
                    {asset.actionType === "View" ? (
                      <>
                        <Eye size={16} className="mr-2" />
                        View
                      </>
                    ) : (
                      <>
                        <Download size={16} className="mr-2 " />
                        Download
                      </>
                    )}
                  </button>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {selectedResource && (
          <div
            id="Resource"
            className="mt-8 bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 mb-10"
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-green-800 dark:text-primary">
                Features for {selectedResource.title}
              </h3>
              <button
                onClick={closeDetails}
                className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600"
              >
                <X size={20} />
              </button>
            </div>
            <div className="overflow-x-scroll max-w-full">
              <table className="min-w-full border-collapse rounded-lg overflow-hidden">
                <thead className="bg-green-100 dark:bg-green-900">
                  <tr>
                    <th className="p-4 text-left text-green-800 dark:text-green-100">
                      Title
                    </th>
                    <th className="p-4 text-left text-green-800 dark:text-green-100">
                      Description
                    </th>
                    <th className="p-4 text-left text-green-800 dark:text-green-100">
                      URL
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {Array.isArray(selectedResource.features) &&
                  selectedResource.features.length > 0 ? (
                    selectedResource.features.map((feature, index) => (
                      <tr
                        key={index}
                        className={
                          index % 2 === 0
                            ? "bg-green-50 dark:bg-gray-800 dark:text-white"
                            : ""
                        }
                      >
                        <td className="p-4 border-b border-green-100 dark:border-green-800 dark:text-white">
                          {feature.title}
                        </td>
                        <td className="p-4 border-b border-green-100 dark:border-green-800 dark:text-white">
                          {feature.description}
                        </td>
                        <td className="p-4 border-b border-green-100 dark:border-green-800 ">
                          {feature.url && (
                            <a
                              target="_blank"
                              href={feature.url}
                              className="text-blue-600 hover:underline flex items-center flex-wrap"
                            >
                              {feature.url}
                            </a>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={3} className="p-4 text-center text-gray-500">
                        No features available for this resource.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AssetsSection;
